import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import AppToggle from '@/components/atoms/AppToggle';
import { DataTable, type ColumnDef } from '@/components/organism/Table';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import {
  getVditransaction,
  getVditransactionsFailure,
  getVditransactionsSuccess,
  resetVditransactions,
} from '@/store/slices/vdiTransaction.slice';
import { getProvider } from '@/store/slices/provider.slice';
import { IVDITransactionProps } from '@/utils/types/vdiTransaction';
import { getVdiTransactionList } from '@/store/selectors/vdiTransaction.selector';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import Autocomplete from '@/components/elements/AutoComplete';
import { fetchVdiTransactions } from '@/services/vdiTransaction';
import { isEmpty } from 'lodash';
import {
  VDI_TYPES,
  VDI_VERSION,
  GATEWAY_TRANSACTION_PROPS,
} from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  STRINGS,
} from '@/constant/global/global.constant';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import { IVdiVersion } from '@/services/vdiTransaction/vdiTransaction.type';
import { useDateFilter } from '@/context/dateFilter.context';
import { RootState } from '@/store/types';
import useDeepEffect from '@/hooks/useDeepEffect';
import { VdiExpandedPanel } from '@/components/organism/TableExpand/VdiTransactionExpanded';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { VDI_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/vdiTransaction.testIds';
import { buildColumns } from './vdiTransaction.columns';
import {
  DEFAULT_AUTO_REFRESH_INTERVAL,
  MAX_REFRESH_COUNT,
  SMARTCAFE_EVDI_REQUEST_STATUS,
  smartCafetransactionStatusFilters,
  GATEWAYVERSION,
  getProviderConfigBasedOnPartnerId,
} from './constant';

// ── Page component ────────────────────────────────────────────────────────────
const Vditransaction = () => {
  const [isCopy, setIsCopy] = useState('');
  const [searchByRequestId, onSearchByRequestId] = useState('');
  const [type, setType] = useState(VDI_TYPES[0]);
  const [version, setVersion] = useState<IVdiVersion>(VDI_VERSION[1]); // default VDI 2
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [selectedVdiTransactionId, setSelectedVdiTransactionId] = useState('');
  const [sorting, setSorting] = useState({
    orderBy: 'createdOn',
    order: 'desc',
  });
  const [selection, setSelection] = useState<{
    provider: any;
    operator: any;
    location: any;
  }>({ provider: null, operator: null, location: null });
  const [transactionStatus, setTransactionStatus] = useState(
    smartCafetransactionStatusFilters[0],
  );

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(DEFAULT_AUTO_REFRESH_INTERVAL);
  const refreshCountRef = useRef(0);
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [isMsgOpen, setIsMsgOpen] = useState<Record<string, boolean>>({});
  const isInitialized = useRef(false);
  const hasLoaded = useRef(false);

  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;

  const toggleMsgOpen = useCallback((id: string) => {
    setIsMsgOpen((prev) => {
      const resetState = Object.keys(prev).reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {},
      );
      return { ...resetState, [id]: !prev[id] };
    });
  }, []);

  const requestIdQuery = useDebounce(
    searchByRequestId?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const dispatch = useDispatch();

  const {
    vditransactions: { data, pagination: paginationResponse },
    vditransaction,
  } = useSelector(getVdiTransactionList) as any;
  const vdiTransactionState = useSelector((state: RootState) => state.vdiTransaction as any);
  const providerState = useSelector((state: RootState) => state.provider);
  const providersListArray = providerState?.providers?.data || [];
  const selectedProviderId = providerState?.provider?.providerId;

  const totalPages = useMemo(
    () => Math.ceil(paginationResponse?.totalRecords / pagination.limit),
    [paginationResponse?.totalRecords, pagination.limit],
  );

  const expandedRowIndex = useMemo<number | null>(() => {
    if (!data?.length) return null;
    const idx = (data as IVDITransactionProps[]).findIndex(
      (row) => isMsgOpen[row?.vdiTransactionId] === true,
    );
    return idx >= 0 ? idx : null;
  }, [data, isMsgOpen]);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((prev) => ({ ...prev, limit: value }));
      if (totalPages < pagination?.page)
        setPagination((prev) => ({ ...prev, page: totalPages }));
    },
    [pagination?.page, totalPages],
  );

  const handlePreviousClick = useCallback(
    (page: number) => {
      if (page > 1 && page <= totalPages)
        setPagination((prev) => ({ ...prev, page: page - 1 }));
    },
    [totalPages],
  );

  const handleNextClick = useCallback(
    (page: number) => {
      if (page >= 1 && page < totalPages)
        setPagination((prev) => ({ ...prev, page: page + 1 }));
    },
    [totalPages],
  );

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 1500);
  }, []);

  const isProviderHavingConfig = (item: IVDITransactionProps) => {
    return getProviderConfigBasedOnPartnerId(
      item?.partnerId,
      providersListArray,
    );
  };

  const onHandleClick = useCallback(
    (id: string, item: IVDITransactionProps, providerId?: string) => {
      const isCurrentlyOpen = isMsgOpen[id];
      const hasConfig = isProviderHavingConfig(item);
      toggleMsgOpen(id);

      // If details for this same row are already present, avoid forcing loader state.
      const hasDetailAlready = vditransaction?.vdiTransactionId === id;
      if (!isCurrentlyOpen) {
        setSelectedVdiTransactionId(id);

        const shouldFetchProvider =
          !!providerId && hasConfig && selectedProviderId !== providerId;

        // Disable (show loader) if either byId call will be made
        setIsButtonDisabled(!hasDetailAlready || shouldFetchProvider);

        if (!hasDetailAlready) {
          dispatch(getVditransaction({ id, version: version?.id }));
        }

        if (shouldFetchProvider && providerId) {
          dispatch(getProvider(providerId));
        }
      } else {
        // Closing expanded row should always clear row-level loading state.
        setIsButtonDisabled(false);
      }
    },
    [
      dispatch,
      isMsgOpen,
      selectedProviderId,
      toggleMsgOpen,
      version?.id,
      vditransaction?.vdiTransactionId,
    ],
  );

  const vdiTransactionParams = useMemo(
    () => ({
      ...pagination,
      ...(requestIdQuery ? { vdiTransactionId: requestIdQuery } : {}),
      ...(type && type?.id !== '1' ? { type: type?.id } : {}),
      ...(selection?.operator && selection?.operator?.sourceId !== '1'
        ? { operatorId: selection?.operator?.sourceId }
        : {}),
      version: version ? version?.id : VDI_VERSION[0].id,
      ...(startDate && { createdStartDate: startDate }),
      ...(endDate && { createdEndDate: endDate }),
      ...(transactionStatus?.queryParam &&
        transactionStatus.queryParam !== SMARTCAFE_EVDI_REQUEST_STATUS.ALL
        ? { status: transactionStatus.queryParam }
        : {}),
      ...(selection?.provider?.id ? { provider: selection?.provider?.id } : {}),
    }),
    [
      pagination,
      requestIdQuery,
      type?.id,
      selection?.operator,
      selection?.provider?.id,
      version?.id,
      startDate,
      endDate,
      transactionStatus?.queryParam,
    ],
  );

  const fetchTransaction = useCallback(
    async (param: any) => {
      setLoading(true);
      try {
        const res = await fetchVdiTransactions({ ...param, ...sorting });
        dispatch(getVditransactionsSuccess(res?.data as any));
      } catch (error) {
        dispatch(getVditransactionsFailure());
      } finally {
        setLoading(false);
      }
      setIsMsgOpen({});
    },
    [dispatch, sorting],
  );

  // Auto-refresh countdown
  useEffect(() => {
    if (!autoRefresh) return;
    setCountdown(DEFAULT_AUTO_REFRESH_INTERVAL);
    const interval = setInterval(
      () => setCountdown((prev) => (prev > 0 ? prev - 1 : 0)),
      1000,
    );
    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      refreshCountRef.current += 1;
      fetchTransaction(vdiTransactionParams);
      setCountdown(DEFAULT_AUTO_REFRESH_INTERVAL);
      if (refreshCountRef.current >= MAX_REFRESH_COUNT) {
        setAutoRefresh(false);
        clearInterval(interval);
        setCountdown(DEFAULT_AUTO_REFRESH_INTERVAL);
      }
    }, DEFAULT_AUTO_REFRESH_INTERVAL * 1000);
    return () => {
      clearInterval(interval);
      refreshCountRef.current = 0;
      setCountdown(DEFAULT_AUTO_REFRESH_INTERVAL);
    };
  }, [autoRefresh, fetchTransaction, transactionStatus]);

  // Clear button disabled when detail loads
  useEffect(() => {
    return () => {
      dispatch(resetVditransactions());
    };
  }, []);

  // Load version from localStorage — default to VDI 2 when nothing is stored
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(GATEWAYVERSION) ?? 'null');
    // If the user previously explicitly chose VDI 1, honour that; otherwise default to VDI 2 (index 1)
    const versionSet = stored?.id === VDI_VERSION[0].id ? 0 : 1;
    setVersion(VDI_VERSION[versionSet]);
  }, []);

  // Page guard
  useEffect(() => {
    if (totalPages > 0 && pagination?.page > totalPages) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [totalPages, pagination?.page]);

  // Track first successful data load for skeleton vs refetch indicator
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  useDeepEffect(
    () => {
      const loadingData = {
        provider: selection?.provider?.id,
        version: version?.id,
        operator: selection?.operator,
      };
      if (Object.values(loadingData).some((v) => !v)) return;
      if (!isInitialized.current) {
        isInitialized.current = true;
        if (version?.id) fetchTransaction(vdiTransactionParams);
        return;
      }
      if (version?.id) fetchTransaction(vdiTransactionParams);
    },
    [
      pagination,
      requestIdQuery,
      selection?.provider?.id,
      selection?.operator,
      type?.id,
      version?.id,
      sorting.order,
      sorting.orderBy,
      transactionStatus?.queryParam,
      startDate,
      endDate,
    ],
    true,
  );

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick, providersListArray),
    [isCopy, onCopyClick, providersListArray],
  );

  const getExpandedPanelState = useCallback(
    (item: IVDITransactionProps) => {
      const isSelectedRow =
        selectedVdiTransactionId === item.vdiTransactionId;
      const hasProviderConfig = isProviderHavingConfig(item);
      const providerFetchWasTriggeredForRow =
        !!item?.providerId &&
        hasProviderConfig &&
        selectedProviderId !== item.providerId;

      // Show transaction failure only for the currently expanded row.
      const shouldShowDetailFailed =
        !!vdiTransactionState?.singleError &&
        isSelectedRow;

      // Show provider failure only when provider-by-id was actually required for this row.
      const shouldShowProviderFailed =
        !!providerState?.singleError &&
        providerFetchWasTriggeredForRow &&
        isSelectedRow;

      // Keep row spinner scoped to this row while any by-id request is in-flight.
      const shouldShowExpandedLoading =
        isButtonDisabled &&
        (!!vdiTransactionState?.singleLoading ||
          !!providerState?.singleLoading) &&
        isSelectedRow;

      return {
        hasProviderConfig,
        shouldShowDetailFailed,
        shouldShowProviderFailed,
        shouldShowExpandedLoading,
      };
    },
    [
      isButtonDisabled,
      providerState?.singleError,
      providerState?.singleLoading,
      selectedProviderId,
      selectedVdiTransactionId,
      vdiTransactionState?.singleError,
      vdiTransactionState?.singleLoading,
      providersListArray,
    ],
  );

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.VDI_TXN}
          subtitle={STRINGS.VDI_TXN_SUB}
          icon={getNavItemIcon(NAV_ITEMS.VDI_TRANSACTIONS)}
          actionsSlot={
            <GlobalSearch
              className="w-[280px]"
              placeholder={
                GATEWAY_TRANSACTION_PROPS.VDITRANSACTION
                  .REQUEST_ID_PLACEHOLDER
              }
              dataTestId={tid(TID.SEARCH_INPUT)}
              globalSearch={searchByRequestId}
              setGlobalSearch={(v: string) => {
                onSearchByRequestId(v);
                setIsMsgOpen({});
              }}
            />
          }
          leadingActions={
            <div className="flex items-center gap-2">
              {autoRefresh && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {countdown}s
                </span>
              )}
              <AppToggle
                data-testid={tid(TID.AUTO_REFRESH_TOGGLE)}
                checked={autoRefresh}
                onChange={(v) => setAutoRefresh(v)}
                color="primary"
                size="sm"
                label={
                  GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.AUTO_REFRESH_LABEL
                }
                labelPosition="left"
              />
            </div>
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={(v: any) => {
                setSelection(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              showProvider
              showLocation={false}
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={
            <div className="flex items-center gap-2">
              <div className="w-[140px]">
                <Autocomplete
                  label={GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.TYPE}
                  placeholder={
                    GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.SELECT_TYPE
                  }
                  dataTestId={tid(TID.TYPE_FILTER)}
                  selectedValue={type}
                  handleSelectedValue={(v: any) => {
                    setType(v);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  lists={VDI_TYPES.slice(0, 5)}
                />
              </div>
              <div className="w-[140px]">
                <Autocomplete
                  label={GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.STATUS}
                  placeholder={
                    GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.SELECT_STATUS
                  }
                  dataTestId={tid(TID.STATUS_FILTER)}
                  handleSelectedValue={(v: any) => {
                    setTransactionStatus(v);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  selectedValue={transactionStatus}
                  lists={smartCafetransactionStatusFilters}
                />
              </div>
              <div className="h-6 w-px bg-gray-200 dark:bg-gray-700/60" />
              <SegmentedControl
                testId={tid(TID.VERSION_SELECT)}
                options={VDI_VERSION.map((v) => ({
                  value: v.id,
                  label: v.name,
                }))}
                value={version?.id}
                onChange={(val) => {
                  const found = VDI_VERSION.find((v) => v.id === val);
                  if (found) {
                    localStorage.setItem(GATEWAYVERSION, JSON.stringify(found));
                    setVersion(found);
                    setType(VDI_TYPES[0]);
                    setIsMsgOpen({});
                    setPagination((p) => ({ ...p, page: 1 }));
                  }
                }}
                size="lg"
                shape="square"
                variant="gradient"
              />
            </div>
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={(data ?? []) as IVDITransactionProps[]}
        columns={columns}
        loading={loading}
        skeletonRows={15}
        sortState={sorting as any}
        onSort={(orderBy, order) => {
          setSorting({ orderBy: String(orderBy), order });
          setIsMsgOpen({});
        }}
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        expandedRowIndex={expandedRowIndex}
        onExpandChange={(_idx, item) => {
          if (item) {
            const row = item as IVDITransactionProps;
            onHandleClick(row.vdiTransactionId,item, row.providerId);
          } else {
            setIsMsgOpen({});
            setIsButtonDisabled(false);
          }
        }}
        renderExpandedRow={(item: IVDITransactionProps, rowIndex: number) => {
          const {
            hasProviderConfig,
            shouldShowDetailFailed,
            shouldShowProviderFailed,
            shouldShowExpandedLoading,
          } = getExpandedPanelState(item);

          return (
            <VdiExpandedPanel
              data={item}
              rowIndex={rowIndex}
              hasProviderConfig={hasProviderConfig}
              detailFailed={shouldShowDetailFailed}
              providerFailed={shouldShowProviderFailed}
              loading={shouldShowExpandedLoading}
            />
          );
        }}
        rowKey={(item, idx) =>
          (item as IVDITransactionProps)?.vdiTransactionId ?? String(idx)
        }
        rowTestId={(item: any, idx: number) =>
          tid(TID.TABLE_ROW(`${idx}`))
        }
      />
    </FullPageCard>
  );
};

export default Vditransaction;
